<template>
  <div class="flex-auto ce-search">
    <div class="ce-productDetails ce-container">
      <main>
        <div
          v-if="backlink"
          class="ce-backCta">
          <a :href="backlink">
            <fa-icon icon="long-arrow-alt-left" />
            {{ $t('productDetail.backCta') }}
          </a>
        </div>

        <template
          v-if="pigment">
          <SimpleGallery
            :images="galleryImages"
            :index="galleryIndex"
            @close="galleryIndex = null" />

          <div class="flex justify-between">
            <div>
              <div class="sm-flex justify-between">
                <div class="flex ce-productDetails-header">
                  <div
                    v-if="image"
                    class="ce-productColorBadge mr4">
                    <img
                      v-if="isBase64Image"
                      :src="image"
                      width="140"
                      height="80">
                    <div
                      v-if="!isBase64Image"
                      class="ce-backgroundImageContainer"
                      :style="{'background-image': !isBase64Image ? 'url(' + image + ')' : ''}"
                      @click="showColorChip()">
                      <div>
                        <fa-icon icon="search-plus" />
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-column justify-center">
                    <div
                      class="ce-statusHint"
                      v-if="pigment.isActive === false || pigment.isInternal">
                      <fa-icon icon="exclamation-triangle" />
                      {{ `${pigment.isActive === false ? $t('productDetail.isInactive') : $t('productDetail.isInternal')}` }}
                    </div>
                    <h1>
                      {{ pigment.product_name }}

                      <span
                        class="productAttributeImage"
                        v-if="showComfortIcon">
                        <img
                          src="https://assets-static.colormaterials.sunchemical.com/data-fields/comfort.jpg"
                          alt="Comfort">
                      </span>

                      <span
                        class="productAttributeImage"
                        v-if="showPeaceOfMindIcon">
                        <img
                          src="https://assets-static.colormaterials.sunchemical.com/data-fields/peace_of_mind.jpg"
                          alt="Peace of Mind">
                      </span>
                    </h1>

                    <div
                      class="ce-changeIndustryHint mt2">
                      <i18n-t
                        keypath="productDetail.forIndustry"
                        tag="span"
                        scope="global">
                        <strong>{{ activeIndustryInfo.title }}</strong>
                      </i18n-t>

                      <a
                        v-if="hasMultipleIndustries"
                        href=""
                        class="ce-btn ce-btn-light ce-btn--tiny"
                        @click.prevent="showIndustrySelect = true">
                        {{ $t('productDetail.changeIndustryCta') }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex mt3 ce-pageActions no-print">
                <a
                  href=""
                  @click.prevent="triggerPrinting">
                  <fa-icon
                    icon="print" />
                  <span>{{ $t('productDetail.printCta') }}</span>
                </a>

                <a
                  href=""
                  @click.prevent="savePage">
                  <fa-icon
                    :icon="['far', 'file-pdf']" />
                  <span>{{ $t('productDetail.savePageCta') }}</span>
                </a>

                <request-sample-form-modal
                  v-if="canRequestSample"
                  :headline="$t('requestSampleForm.modalHeadline')"
                  :description="$t('requestSampleForm.modalDescription', { productName: pigment.product_name })"
                  :additional-data="contactFormInfo">
                  <a
                    href="">
                    <fa-icon icon="vial" />
                    {{ $t('requestSampleForm.openCta') }}
                  </a>
                </request-sample-form-modal>

                <a
                  v-if="!isOnCompareList"
                  href=""
                  :class="{disabled: !canAddToComparison}"
                  @click.prevent="addToComparison">
                  <tooltip
                    v-if="comparisonListTooltip"
                    :content="comparisonListTooltip">
                    <fa-layers>
                      <fa-icon icon="list" />
                      <fa-icon
                        icon="plus-circle"
                        transform="shrink-4 up-7 right-10" />
                    </fa-layers>
                    <span class="ml1 ce-pageActionLabel">{{ $t('productDetail.addToComparisonCta') }}</span>
                  </tooltip>
                  <template v-else>
                    <fa-layers>
                      <fa-icon icon="list" />
                      <fa-icon
                        icon="plus-circle"
                        transform="shrink-4 up-7 right-10" />
                    </fa-layers>
                    <span class="ml1 ce-pageActionLabel">{{ $t('productDetail.addToComparisonCta') }}</span>
                  </template>
                </a>
                <a
                  v-if="isOnCompareList"
                  href=""
                  @click.prevent="$store.dispatch('productComparison/openBasket')">
                  <fa-layers>
                    <fa-icon
                      class="ce-colorSuccess"
                      icon="list" />
                    <fa-icon
                      icon="check"
                      class="ce-colorSuccess"
                      transform="shrink-2 up-7 right-10" />
                  </fa-layers>
                  <span class="ml1 ce-pageActionLabel ce-colorSuccess">{{ $t('productDetail.inComparisonCta') }}</span>
                </a>

                <a
                  v-if="activeUser"
                  href=""
                  :class="{'ce-colorSuccess': hasDocumentFavorite || hasPartialDocumentFavorite}"
                  @click.prevent="toggleDocumentFavorite">
                  <fa-icon :icon="documentFavoriteStateIcon" />

                  {{ documentFavoriteStateLabel }}
                </a>

                <a
                  v-if="canEditPigment"
                  :href="`admin/pigments/CE-ServicePlatform-Pigment/EditForm/field/CE-ServicePlatform-Pigment/item/${pigment.id}/edit`"
                  target="_blank">
                  <fa-icon icon="pencil-alt" />

                  {{ $t('productDetail.editCta') }}
                </a>
              </div>
            </div>
            <div
              v-if="productBaseInfo.qrCode && !disableQrCodes"
              class="ce-qrContainer">
              <div>
                <a
                  href="#"
                  @click.prevent="showQR()">
                  <img
                    :src="productBaseInfo.qrCode"
                    height="100">
                </a>
                <div>
                  <fa-icon icon="mobile-alt" />
                  {{ $t('productDetail.qr.info') }}
                  <fa-icon icon="long-arrow-alt-right" />
                </div>
              </div>
              <div class="text-right">
                <a
                  href="https://colormaterials.sunchemical.com/pigment-viewer-mobile-app/"
                  target="_blank"
                  class="ms-2 inline-block mt2"
                  v-html="$t('productDetail.qr.cta')" />
              </div>
            </div>
          </div>

          <div
            class="mt3 ce-productInfo">
            <div>
              <div
                v-if="brandDescription"
                :class="{'mb4': !!description}">
                <h2 class="ce-productDetailsHeadline">
                  {{ $t('productDetail.brandDescriptionHeadline') }}
                </h2>

                <div
                  class="pre-wrap"
                  v-html="brandDescription" />
              </div>

              <template v-if="description">
                <h2 class="ce-productDetailsHeadline">
                  {{ $t('productDetail.descriptionHeadline') }}
                </h2>

                <div
                  v-html="description.value" />
              </template>
            </div>

            <div>
              <div
                v-if="isValidated"
                class="ce-sunSupportInfo">
                <h2>Online Ordering Through SunSupport Online</h2>
                <p>
                  <strong>Ready to place an order?</strong><br>
                  Access or register for our online ordering platform at
                  <a
                    href="https://www.sunsupportonline.com"
                    target="_blank"
                    rel="noopener">www.sunsupportonline.com</a>
                </p>
                <p>
                  <strong>Please Note:</strong>
                  If you need to purchase new products that are not currently listed in your catalog, please contact <strong>Customer Service</strong>
                  through <strong>SunSupport Online.</strong> Our team will add the requested item(s) to your catalog before you submit your order
                </p>
              </div>

              <div
                class="ce-internalInfo ce-productDataTable"
                v-if="isInternal && internalInfoDataTable && internalInfoDataTable.length > 0">
                <h2 class="ce-productDetailsHeadline">
                  {{ $t('productDetail.internalInfoHeadline') }}
                </h2>

                <table class="mb5">
                  <tbody>
                    <product-data-field
                      v-for="(field, index) in internalInfoDataTable"
                      :style="(index + 1) % 2 ? 'background: ' + fadedBgColor : ''"
                      :key="field.code"
                      :field="field" />
                  </tbody>
                </table>
              </div>

              <div
                class="ce-contactInfo ce-productDataTable">
                <h2 class="ce-productDetailsHeadline">
                  {{ $t('productDetail.contactHeadline') }}
                </h2>

                <table class="mb5">
                  <tr
                    v-if="onlyGenericContact"
                    :style="'background:' + fadedBgColor">
                    <td>{{ $t('productDetail.contact.bce.label') }}</td>
                    <td
                      class="ce-actionCell"
                      colspan="2">
                      <contact-form-modal
                        :headline="$t('productDetail.contact.bce.modalHeadline')"
                        :description="contactFormDescription"
                        :additional-data="contactFormInfo"
                        contact-type="bce">
                        <fa-icon :icon="['far', 'envelope']" />
                        <span>{{ $t('productDetail.contact.sendInquiry') }}</span>
                      </contact-form-modal>
                    </td>
                  </tr>

                  <!-- TODO temporary removed due to lots of personal changes in the Customer Service and Account Managing responsibilities
                  <template
                    v-if="isValidated">
                    <tr
                      v-if="activeUser.customerService"
                      :style="'background:' + fadedBgColor">
                      <td>{{ $t('productDetail.contact.customerService.label') }}</td>
                      <td class="ce-actionCell">
                        <contact-form-modal
                          :headline="$t('productDetail.contact.customerService.modalHeadline')"
                          :description="contactFormDescription"
                          :additional-data="contactFormInfo"
                          contact-type="customer_service">
                          <fa-icon :icon="['far', 'envelope']" />
                          <span>{{ $t('productDetail.contact.sendInquiry') }}</span>
                        </contact-form-modal>
                      </td>
                      <td class="ce-actionCell">
                        <a :href="`tel:${activeUser.customerService.phone}`">
                          <fa-icon
                            icon="headset" />
                          {{ activeUser.customerService.phone }}
                        </a>
                      </td>
                    </tr>

                    <tr
                      v-if="activeUser.accountManager"
                      :style="'background:' + fadedBgColor">
                      <td>{{ $t('productDetail.contact.accountManager.label') }}</td>
                      <td class="ce-actionCell">
                        <contact-form-modal
                          :headline="$t('productDetail.contact.accountManager.modalHeadline')"
                          :description="contactFormDescription"
                          :additional-data="contactFormInfo"
                          contact-type="account_manager">
                          <fa-icon :icon="['far', 'envelope']" />
                          {{ $t('productDetail.contact.sendInquiry') }}
                        </contact-form-modal>
                      </td>
                      <td class="ce-actionCell">
                        <a :href="`tel:${activeUser.accountManager.phone}`">
                          <fa-icon
                            icon="headset" />
                          {{ activeUser.accountManager.phone }}
                        </a>
                      </td>
                    </tr>
                  </template>-->

                  <template v-if="isValidated">
                    <tr :style="'background:' + fadedBgColor">
                      <td>{{ $t('productDetail.contact.psr.label') }}</td>
                      <td
                        class="ce-actionCell"
                        colspan="2">
                        <contact-form-modal
                          :headline="$t('productDetail.contact.psr.modalHeadline')"
                          :description="contactFormDescription"
                          :additional-data="contactFormInfo"
                          contact-type="psr">
                          <fa-icon :icon="['far', 'envelope']" />
                          {{ $t('productDetail.contact.sendInquiry') }}
                        </contact-form-modal>
                      </td>
                    </tr>
                  </template>

                  <template v-if="contactDataTable && contactDataTable.length > 0">
                    <tr
                      v-for="contactField in contactDataTable"
                      :key="contactField.code"
                      :style="'background:' + fadedBgColor">
                      <td>
                        {{ contactField.title }}

                        <fa-icon
                          v-if="showInternalHint(contactField)"
                          transform="up-6 shrink-4"
                          icon="lock" />
                      </td>
                      <td
                        class="ms-1"
                        colspan="2">
                        {{ contactField.value }}
                      </td>
                    </tr>
                  </template>

                  <tr
                    class="ce-upsellHint"
                    :style="'background:' + fadedBgColor"
                    v-if="showUpsellHint">
                    <td colspan="2">
                      <template v-if="!activeUser">
                        {{ $t('productDetail.upsellHint.register.hint') }}
                        <a
                          href=""
                          @click.prevent="showRegistration">
                          {{ $t('productDetail.upsellHint.register.registerCta') }}
                        </a>
                      </template>
                      <template v-else>
                        <i18n-t
                          keypath="productDetail.upsellHint.validate.hint"
                          scope="global">
                          <a
                            href=""
                            @click.prevent="showUserValidationModal = true">
                            {{ $t('productDetail.upsellHint.validate.cta') }}</a>
                        </i18n-t>
                      </template>
                    </td>
                  </tr>
                  <slot name="contact" />
                </table>
              </div>
            </div>
          </div>

          <div
            class="mt4 ce-productInfo ce-productInfo-details">
            <div>
              <div class="ce-productDataTable">
                <h2 class="ce-productDetailsHeadline">
                  {{ $t('productDetail.detailsHeadline') }}
                </h2>
                <table>
                  <tbody>
                    <product-data-field
                      v-for="(field, index) in productDetailsDataTable"
                      :style="(index + 1) % 2 ? 'background: ' + fadedBgColor : ''"
                      :key="field.code"
                      :field="field" />
                  </tbody>
                </table>

                <div class="ce-hierarchyLegend">
                  <div>
                    <span class="ce-hierarchyIndicator highly_recommended" />
                    {{ $t('productDetail.hierarchyOptionsLegend.highlyRecommended') }}
                  </div>
                  <div>
                    <span class="ce-hierarchyIndicator recommended" />
                    {{ $t('productDetail.hierarchyOptionsLegend.recommended') }}
                  </div>
                  <div>
                    <span class="ce-hierarchyIndicator potentially_recommended" />
                    {{ $t('productDetail.hierarchyOptionsLegend.potentiallyRecommended') }}
                  </div>
                </div>

                <div
                  class="mt5"
                  v-if="specificRequirementsDataTable.length > 0">
                  <h2 class="ce-productDetailsHeadline">
                    {{ $t('productDetail.specificRequirementsHeadline') }}
                  </h2>
                  <table>
                    <tbody>
                      <product-data-field
                        v-for="(field, index) in specificRequirementsDataTable"
                        :style="(index + 1) % 2 ? 'background: ' + fadedBgColor : ''"
                        :key="field.code"
                        :field="field" />
                    </tbody>
                  </table>
                </div>

                <div
                  class="mt5"
                  v-if="sustainabilityDataTable.length > 0">
                  <h2 class="ce-productDetailsHeadline">
                    {{ $t('productDetail.sustainabilityHeadline') }}
                  </h2>
                  <table>
                    <tbody>
                      <product-data-field
                        v-for="(field, index) in sustainabilityDataTable"
                        :style="(index + 1) % 2 ? 'background: ' + fadedBgColor : ''"
                        :key="field.code"
                        :field="field" />
                    </tbody>
                  </table>
                </div>

                <div
                  class="mt5"
                  v-if="regulatoryInformationDataTable.length > 0">
                  <h2 class="ce-productDetailsHeadline">
                    {{ $t('productDetail.regulatoryInfoHeadline') }}
                  </h2>
                  <table>
                    <tbody>
                      <product-data-field
                        v-for="(field, index) in regulatoryInformationDataTable"
                        :style="(index + 1) % 2 ? 'background: ' + fadedBgColor : ''"
                        :key="field.code"
                        :field="field" />
                    </tbody>
                  </table>
                </div>

                <formulations-list
                  :table-bg-color="fadedBgColor" />

                <AdvancedComparisonsForProduct
                  class="mt5"
                  :product="pigment"
                  :faded-bg-color="fadedBgColor"
                  show-headline />
              </div>
            </div>

            <div v-if="technicalInfoDataTable.length > 0 || hasAssets">
              <div
                v-if="technicalInfoDataTable.length > 0"
                class="ce-productDataTable mb5">
                <h2 class="ce-productDetailsHeadline">
                  {{ $t('productDetail.technicalInfoHeadline') }}
                </h2>
                <table>
                  <tbody>
                    <product-data-field
                      v-for="(field, index) in technicalInfoDataTable"
                      :style="(index + 1) % 2 ? 'background: ' + fadedBgColor : ''"
                      :key="field.code"
                      :field="field" />
                  <!-- TODO limit to 10, add "show all" action -->
                  </tbody>
                </table>
              </div>

              <product-assets
                v-if="hasAssets" />
            </div>
          </div>

          <TechnicalProperties
            :product="pigment"
            :color="color" />
        </template>
      </main>
    </div>

    <modal
      v-if="showIndustrySelect"
      class="ce-industrySelectModal"
      headline="Choose your industry"
      :closable="!!this.pigment"
      @close="showIndustrySelect = false">
      <div class="ce-modalContent">
        <div
          class="mxn1 flex flex-wrap center justify-center">
          <div
            class="ce-industry"
            v-for="industry in pigmentIndustries"
            :key="industry.code"
            @click.prevent="changeIndustry(industry.code)"
            :class="{active: activeIndustry === industry.code }">
            <div>
              <img
                v-if="industry.image"
                :src="industry.image.squarePreview.src"
                :alt="industry.title">
              <template v-else>
                <fa-icon icon="industry" />
              </template>
            </div>
            <div class="mt1 bold">
              {{ industry.title }}
            </div>
          </div>
        </div>
      </div>
    </modal>

    <basket-industry-modal
      v-if="addToBasketTemp" />

    <user-validation-modal
      v-if="showUserValidationModal"
      @close="showUserValidationModal = false" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import ProductDataField from 'components/product-detail/ProductDataField.vue';
import RequestSampleFormModal from 'components/forms/RequestSampleFormModal.vue';
import BasketIndustryModal from 'components/product-comparison/BasketIndustryModal.vue';
import ProductAssets from 'components/product-detail/ProductAssets.vue';
import UserValidationModal from 'components/UserValidationModal.vue';
import FormulationsList from 'components/product-detail/FormulationsList.vue';
import { cloneObject, sortArray } from 'utils';
import SimpleGallery from 'components/SimpleGallery.vue';
import TechnicalProperties from 'components/competitor-app-v2/technical-properties/TechnicalProperties.vue';
import AdvancedComparisonsForProduct from 'components/competitor-app-v2/advanced-comparisons/AdvancedComparisonsForProduct.vue';

export default {
  data() {
    return {
      showIndustrySelect: false,
      galleryIndex: null,
      showUserValidationModal: false
    };
  },
  components: {
    AdvancedComparisonsForProduct,
    ProductDataField,
    ProductAssets,
    RequestSampleFormModal,
    BasketIndustryModal,
    UserValidationModal,
    FormulationsList,
    SimpleGallery,
    TechnicalProperties,
  },
  computed: {
    ...mapState([
      'productBaseInfo', 'activeIndustry', 'pigment', 'fieldManifest', 'industries',
      'productGroups', 'excludeFromDataTable', 'backlink', 'documentFavorite',
      'regionList'
    ]),
    ...mapGetters(['nonImageAssets']),
    ...mapState('user', ['activeUser']),
    ...mapGetters('user', ['isValidated', 'isInternal', 'canRequestSample', 'userRoles']),
    ...mapGetters('productComparison', {
      compareListProductUids: 'productUids'
    }),
    ...mapState('productComparison', ['activeComparison', 'addToBasketTemp']),
    ...mapGetters('productComparison', ['basketLimitReached', 'activeComparisonIndustry']),
    ...mapState('generic', ['services', 'disableQrCodes']),
    pigmentData() {
      const list = {};
      if (!this.pigment) return null;
      const industryCodes = this.industries.map((industry) => industry.code);

      Object.keys(this.pigment)
        .forEach((code) => {
          if (['ul_prospector_link'].indexOf(code) > -1) {
            return;
          }

          const productGroupOptions = {};
          this.productGroups.forEach((pg) => {
            productGroupOptions[pg.code] = pg.title;
          });
          if (code === 'product_group') {
            if (this.pigment[code]) {
              list[code] = {
                code,
                title: this.$t('generic.productGroupTitle'),
                value: this.pigment[code],
                options: productGroupOptions
              };
            }
          }

          if (industryCodes.indexOf(code) > -1) {
            Object.keys(this.pigment[code])
              .forEach((codeInIndustry) => {
                const conf = this.fieldManifest[codeInIndustry];
                const val = this.pigment[code][codeInIndustry];

                if (conf && val !== null && typeof val !== 'undefined' && (typeof val.length !== 'number' || val.length > 0)) {
                  if (codeInIndustry === 'regional_marketing_advice') {
                    conf.options = Object.fromEntries(this.regionList.map((r) => [r.code, r.name]));
                  }

                  list[codeInIndustry] = {
                    ...conf,
                    code: codeInIndustry,
                    value: this.pigment[code][codeInIndustry],
                  };
                }
              });
          } else {
            const conf = this.fieldManifest[code];
            const val = this.pigment[code];

            if (conf && val !== null && typeof val !== 'undefined' && (typeof val.length !== 'number' || val.length > 0)) {
              let value = this.pigment[code];

              if (code === 'availableRegions' && Array.isArray(value) && value.length === this.regionList.filter((r) => r.code !== 'global').length) {
                value = this.$t('productDetail.availableRegions.all');
              }

              list[code] = {
                ...conf,
                code,
                value
              };
            }
          }
        });

      return list;
    },
    dataTable() {
      const fields = [];

      Object.keys(this.pigmentData)
        .forEach((code) => {
          if (this.excludeFromDataTable.indexOf(code) === -1) {
            fields.push(this.pigmentData[code]);
          }
        });

      // Sort by title
      fields.sort((left, right) => {
        if (left.title > right.title) return 1;
        if (left.title < right.title) return -1;

        return 0;
      });

      // Assign a sort number (index + 10) for each field, manually push some fields to the beginning of the list
      fields.forEach((f, index) => {
        switch (f.code) {
          case 'color_space':
            f.sort = 1;
            break;
          case 'product_group':
            f.sort = 2;
            break;
          case 'brand':
            f.sort = 3;
            break;
          case 'color_index':
            f.sort = 4;
            break;
          case 'availableRegions':
            f.sort = 30;
            break;
          case 'regional_marketing_advice':
            f.sort = 31;
            break;
          default:
            f.sort = index + 10;
        }
      });

      // Finally sort by the sort number
      fields.sort((left, right) => {
        if (left.sort > right.sort) return 1;
        if (left.sort < right.sort) return -1;

        return 0;
      });

      return fields;
    },
    productDetailsDataTable() {
      return this.dataTable.filter((field) => {
        if (this.fieldManifest.hasOwnProperty(field.code)) {
          return this.fieldManifest[field.code].informationType === 'product_detail';
        }

        return false;
      });
    },
    specificRequirementsDataTable() {
      return this.dataTable.filter((field) => {
        if (this.fieldManifest.hasOwnProperty(field.code)) {
          return this.fieldManifest[field.code].informationType === 'specific_requirements';
        }

        return false;
      });
    },
    regulatoryInformationDataTable() {
      return this.dataTable.filter((field) => {
        if (this.fieldManifest.hasOwnProperty(field.code)) {
          return this.fieldManifest[field.code].informationType === 'regulatory_info';
        }

        return false;
      });
    },
    sustainabilityDataTable() {
      return this.dataTable.filter((field) => {
        if (this.fieldManifest.hasOwnProperty(field.code)) {
          return this.fieldManifest[field.code].informationType === 'sustainability';
        }

        return false;
      });
    },
    technicalInfoDataTable() {
      return this.dataTable.filter((field) => {
        if (this.fieldManifest.hasOwnProperty(field.code)) {
          return this.fieldManifest[field.code].informationType === 'technical_info';
        }

        return false;
      });
    },
    internalInfoDataTable() {
      return this.dataTable.filter((field) => {
        if (this.fieldManifest.hasOwnProperty(field.code)) {
          return this.fieldManifest[field.code].informationType === 'internal_info';
        }

        return false;
      });
    },
    // TODO should be shown in the description section, clarify which layout/form this should have (table, content only...?)
    // --> maintenance only for now, so actually not shown in the FE
    descriptionDataTable() {
      return this.dataTable.filter((field) => {
        if (this.fieldManifest.hasOwnProperty(field.code)) {
          return this.fieldManifest[field.code].informationType === 'description';
        }

        return false;
      });
    },
    contactDataTable() {
      return this.dataTable.filter((field) => {
        if (this.fieldManifest.hasOwnProperty(field.code)) {
          return this.fieldManifest[field.code].informationType === 'contact';
        }

        return false;
      });
    },
    hasMultipleIndustries() {
      return this.pigment.industries.length > 1;
    },
    pigmentIndustries() {
      return this.industries.filter((i) => this.productBaseInfo.industries.indexOf(i.code) > -1);
    },
    activeIndustryInfo() {
      if (this.activeIndustry) {
        return this.industries.find((i) => i.code === this.activeIndustry);
      }

      return null;
    },
    description() {
      if (this.pigmentData.product_industry_description) {
        return this.pigmentData.product_industry_description;
      }

      if (this.pigmentData.product_description_long) {
        return this.pigmentData.product_description_long;
      }

      if (this.pigmentData.product_description_short) {
        return this.pigmentData.product_description_short;
      }

      return null;
    },
    brandDescription() {
      if (this.pigment.brand && this.fieldManifest?.brand?.optionsConfig) {
        return this.fieldManifest.brand.optionsConfig?.find((o) => o.code === this.pigment.brand)?.description;
      }

      return null;
    },
    image() {
      if (this.activeIndustry && this.pigment.stackPreviewImages?.[this.activeIndustry]?.url) {
        return this.pigment.stackPreviewImages[this.activeIndustry].url;
      }

      if (this.pigment.stackPreviewImages?.generic?.url) {
        return this.pigment.stackPreviewImages.generic.url;
      }

      // handle actual image (image_color_chip asset)
      const colorChipAssets = this.pigment.assets
        && Array.isArray(this.pigment.assets)
        && this.pigment.assets.find((asset) => asset.type === 'image_color_chip');
      if (colorChipAssets) {
        return `${this.services.documentservice.url}/api/v2/document/download/${colorChipAssets.id}`;
      }

      if (this.pigmentData.color_values
        && this.pigmentData.color_values.value
        && this.pigmentData.color_values.value.image) {
        return this.pigmentData.color_values.value.image;
      }

      return null;
    },
    isBase64Image() {
      return this.image && this.image.startsWith('data:');
    },
    hasAssets() {
      return this.hasUlProspectorLink
        || (this.pigment.hasOwnProperty('assets') && Array.isArray(this.pigment.assets) && this.nonImageAssets.length > 0);
    },
    hasUlProspectorLink() {
      return !!this.pigment.ul_prospector_link;
    },
    hasColor() {
      return this.pigment.hasOwnProperty('color_values')
        && typeof this.pigment.color_values === 'object'
        && this.pigment.color_values
        && this.pigment.color_values?.R
        && this.pigment.color_values?.G
        && this.pigment.color_values?.B;
    },
    fadedBgColor() {
      if (!this.hasColor) return '#ebebeb';

      return `rgba(${this.pigment.color_values.R}, ${this.pigment.color_values.G}, ${this.pigment.color_values.B}, .25)`;
    },
    color() {
      if (this.hasColor) {
        return `rgba(${this.pigment.color_values.R}, ${this.pigment.color_values.G}, ${this.pigment.color_values.B})`;
      }

      return null;
    },
    contactFormInfo() {
      if (!this.pigment) return {};

      return {
        prd: this.productBaseInfo.prd,
        product_name: this.pigment.product_name,
        product_id: this.productBaseInfo.id,
        selected_industry: this.activeIndustryInfo.code
      };
    },
    contactFormDescription() {
      if (!this.pigment) return {};

      return `For ${this.pigment.product_name} for ${this.activeIndustryInfo.title} Industry`;
    },
    onlyGenericContact() {
      return true;
      // TODO temporary changed due to lots of personal changes in the Customer Service and Account Managing responsibilities
      // return !this.isValidated;
    },
    canAddToComparison() {
      return !this.isOnCompareList && !this.basketLimitReached && this.isSameIndustryAsComparison;
    },
    isOnCompareList() {
      return this.compareListProductUids.indexOf(this.pigment.uid) > -1;
    },
    isSameIndustryAsComparison() {
      if (!this.activeComparison || !this.activeComparison.industry) return true;

      return this.activeIndustry === this.activeComparison.industry;
    },
    comparisonListTooltip() {
      if (this.canAddToComparison || this.isOnCompareList) return null;

      if (this.basketLimitReached) return this.$t('productComparison.basket.tooltips.limitReached');

      if (!this.isSameIndustryAsComparison) {
        return this.$t('productComparison.basket.tooltips.industryLimit', { industry: this.activeComparisonIndustry.title });
      }

      return null;
    },
    hasDocumentFavorite() {
      if (!this.documentFavorite) return false;

      if (!this.documentFavorite.documentTypes || !Array.isArray(this.documentFavorite.documentTypes)) return true;

      return this.documentFavorite.documentTypes.length === 0;
    },
    hasPartialDocumentFavorite() {
      if (!this.documentFavorite) return false;

      if (!this.documentFavorite.documentTypes || !Array.isArray(this.documentFavorite.documentTypes)) return true;

      return this.documentFavorite.documentTypes.length > 0;
    },
    documentFavoriteStateIcon() {
      if (this.hasDocumentFavorite) return ['fas', 'star'];

      return this.hasPartialDocumentFavorite ? ['fas', 'star-half-alt'] : ['far', 'star'];
    },
    documentFavoriteStateLabel() {
      if (this.hasDocumentFavorite) return this.$t('productDetail.documentFavorite.favored');

      return this.hasPartialDocumentFavorite
        ? this.$t('productDetail.documentFavorite.partiallyFavored')
        : this.$t('productDetail.documentFavorite.notFavored');
    },
    showUpsellHint() {
      if (!this.activeUser) return true;

      if (this.isInternal) return false;

      return !this.isValidated;
    },
    pdfDownloadUrl() {
      let url = `${this.services.backend.url}/util/download/product/pigment::${this.productBaseInfo.id}?i=${this.activeIndustry}`;

      if (this.activeUser) {
        url += `&token=${this.activeUser.token}`;
      }

      return url;
    },
    galleryImages() {
      const imgs = [];

      if (!this.isBase64Image) {
        imgs.push({
          src: this.image,
          id: 'colorChip'
        });
      }

      if (this.productBaseInfo.qrCode) {
        imgs.push({
          title: this.$t('productDetail.qr.info'),
          src: this.productBaseInfo.qrCode,
          id: 'qrCode'
        });
      }

      return imgs;
    },
    showPeaceOfMindIcon() {
      if (this.pigmentData?.sustainability?.value?.includes('peace_of_mind')) {
        return true;
      }

      return false;
    },
    showComfortIcon() {
      if (this.pigmentData?.sustainability?.value?.includes('comfort')) {
        return true;
      }

      return false;
    },
    canEditPigment() {
      if (this.activeUser && this.userRoles) {
        return this.userRoles.includes('pigment-editor')
          || this.userRoles.includes('piving-manager')
          || this.userRoles.includes('document-manager');
      }

      return false;
    }
  },
  created() {
    if (!this.pigment) {
      this.showIndustrySelect = true;
    } else {
      this.trackPageView();
    }

    if (this.activeUser) {
      this.$store.dispatch('fetchDocumentFavorites');
    }
  },
  watch: {
    activeUser() {
      if (this.activeUser) {
        this.$store.dispatch('fetchDocumentFavorites');
      }
    }
  },
  methods: {
    ...mapActions(['saveDocumentFavorite', 'deleteDocumentFavorite']),
    ...mapActions('user', ['showRegistration']),
    trackPageView() {
      this.$trackEvent(
        'Page View',
        'View Product Page',
        {
          url: location.pathname,
          industry: this.activeIndustry,
          prd: this.productBaseInfo.prd
        }
      );
    },
    changeIndustry(industryCode) {
      this.$store.dispatch('changeIndustry', industryCode);
      this.showIndustrySelect = false;
      this.trackPageView();
    },
    triggerPrinting() {
      this.$trackEvent(
        'Product',
        'Print',
        {
          prd: this.productBaseInfo.prd
        }
      );
      window.open(this.pdfDownloadUrl);
    },
    savePage() {
      this.$trackEvent(
        'Product',
        'Save as PDF',
        {
          prd: this.productBaseInfo.prd
        }
      );
      location.href = `${this.pdfDownloadUrl}&download=1`;
    },
    addToComparison() {
      if (!this.canAddToComparison) return;

      this.$store.dispatch('productComparison/addToBasket', {
        product: this.pigment,
        industry: this.activeIndustry
      });
    },
    toggleDocumentFavorite() {
      if (this.hasDocumentFavorite) {
        this.removeDocumentFavorite();
      } else {
        this.addDocumentFavorite();
      }
    },
    addDocumentFavorite() {
      this.saveDocumentFavorite({ documentTypes: [] });
    },
    removeDocumentFavorite() {
      this.deleteDocumentFavorite();
    },
    showInternalHint(dataField) {
      if (dataField.accessLevel && Array.isArray(dataField.accessLevel)) {
        const accessLevels = cloneObject(dataField.accessLevel);

        sortArray(accessLevels, 'weight', 'asc');
        const mostRestrictive = accessLevels.shift();

        if (mostRestrictive && mostRestrictive.code === 'internal') {
          return true;
        }
      }

      return false;
    },
    showGalleryImageById(id) {
      const imageIndex = this.galleryImages.findIndex((i) => i.id === id);
      if (imageIndex > -1) {
        this.galleryIndex = imageIndex;
      }
    },
    showColorChip() {
      this.showGalleryImageById('colorChip');
    },
    showQR() {
      this.showGalleryImageById('qrCode');
    }
  }
};
</script>

<style lang="less">
@import (reference) '~styles/includes/base';

.ce-productDetails {
  padding-bottom: @space-6;

  .light-gallery__text {
    background: transparent !important;
    color: #000 !important;
  }

  .ce-productDetails-header {
    @media screen and (min-width: @breakpoint-sm) {
      padding-right: @space-5;
    }

    @media screen and (max-width: (@breakpoint-xxs - 1)) {
      .ce-productColorBadge {
        width: 60px;
        margin-right: @space-3;
      }
    }
  }

  h2.ce-productDetailsHeadline {
    text-transform: uppercase;
    .ms(1);
    margin: @space-2 0 @space-3;
  }

  h3.ce-productDetailsSubHeadline {
    .ms(-1);
    margin-top: -(@space-2 + @space-1);
    margin-bottom: @space-3;
    font-weight: 400;
    font-family: @font-helvetica-roman;
  }

  .ce-productColorBadge {
    .flex-none;

    .ce-backgroundImageContainer {
      background-repeat: no-repeat;
      background-position: center center;
      background-size: cover;
      width: 140px;
      max-width: 100%;
      height: 80px;
      border-radius: @border-radius-lg;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;

      &:hover {
        cursor: pointer;
      }

      div {
        padding: @space-1 @space-2;
        background: @color-light;
        color: @color-mono-100;
      }
    }

    > img {
      border-radius: @border-radius-lg;
      display: block;
      max-width: 100%;
    }
  }

  .ce-pageActions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    .mxn3;

    > * {
      margin: @space-2 @space-3;
    }

    > a, .ce-requestSampleForm-toggle > a {
      color: @color-text-base;
      .ms(-1);

      &:hover {
        color: @color-light;
      }

      &.disabled {
        cursor: default;
        color: @color-text-base;

        .fa-layers, .ce-pageActionLabel {
          opacity: 0.5;
        }
      }
    }

    a {
      &.ce-colorSuccess {
        color: @color-success;
      }
    }
  }

  .ce-contactInfo {
    table tr {
      &:nth-child(even) {
        background-color: transparent !important;
      }

      td {
        width: calc(100% / 3);
      }
    }

    @media screen and (max-width: @breakpoint-sm) {
      table,
      .ce-actionCell {
        .ms(-2) !important;
      }
    }

    @media screen and (max-width: @breakpoint-xxs) {
      table {
        tr {
          background-color: transparent !important;

          &:first-of-type {
            td:first-child {
              padding-top: 0;
            }
          }

          td {
            width: 100%;
            display: block;

            &:first-child {
              padding-top: @space-4;
              font-weight: bold;
              border-bottom: 1px solid @color-mono-80;
            }
          }
        }
      }
    }
  }

  .ce-sunSupportInfo {
    background: @color-mono-95;
    border: 1px solid @color-mono-90;
    border-left: 4px solid @color-light;
    border-radius: @border-radius;
    padding: @space-3 @space-4;
    margin: @space-2 0 @space-4;
    display: flex;
    flex-direction: column;
    gap: @space-3;

    h2 {
      text-transform: uppercase;
      .ms(1);
      margin: 0;
    }

    p {
      .ms(-1);
      margin: 0;
      line-height: 1.5;

      &:last-child {
        .ms(-2);
        line-height: 1.25;
        color: @color-mono-50;
        margin-bottom: 0;
      }
    }

    a {
      color: @color-light;
      font-weight: bold;
    }
  }

  .ce-upsellHint {
    .ms(1);
    font-weight: bold;
  }

  .ce-productDataTable {
    table {
      border-spacing: 0;
      width: 100%;

      th {
        text-align: left;
        padding: @space-2;
        line-height: 1.5;
      }

      tr td {
        padding: @space-2;
        line-height: 1.5;

        &.ce-actionCell {
          .ms(-1);
          transition: all .25s ease-in-out;

          .ce-contactForm-toggle,
          a {
            color: inherit;

            &:hover {
              color: @color-light;
            }
          }

          > * {
            line-height: 1;
          }
        }
      }
    }
  }

  .ce-statusHint {
    color: @color-error;
    .ms(0);
    font-weight: bold;
    margin-bottom: @space-2;
  }

  h1 {
    margin: 0;
    .ms(2);
    display: flex;
    align-items: flex-end;

    @media screen and (min-width: @breakpoint-xxs) {
      .ms(3);
    }

    @media screen and (min-width: @breakpoint-xs) {
      .ms(4);
    }

    @media screen and (min-width: @breakpoint-sm) {
      .ms(5);
    }

    @media screen and (min-width: @breakpoint-lg) {
      .ms(6);
    }

    .productAttributeImage {
      height: 50px;
      margin-top: -@space-3;
      position: relative;
      top: -@space-2;
      margin-left: @space-2;

      img {
        max-height: 100%;
        display: block;
      }
    }
  }

  .ce-changeIndustryHint {
    .ms(-2);
  }

  .ce-productInfo {
    > div {
      margin-top: @space-5;
    }
  }

  @media screen and (min-width: @breakpoint-lg) {
    .ce-productInfo {
      display: flex;
      .mxn5;

      > div {
        width: 50%;
        margin: 0 @space-5 !important;
      }
    }
  }

  .ce-hierarchyIndicator {
    width: 10px;
    height: 10px;
    display: inline-block;
    border: 1px solid @color-text-base;
    border-radius: 50%;

    &.highly_recommended {
      background: @color-text-base;
    }

    &.recommended {
      background: linear-gradient(90deg, @color-text-base 55%, @color-mono-100 55%);
    }
  }

  .ce-hierarchyLegend {
    display: flex;
    flex-wrap: wrap;
    margin: @space-3 -@space-2 0;
    .ms(-2);

    > div {
      display: flex;
      align-items: center;
      margin: @space-2;

      span {
        margin-right: @space-1;
      }
    }
  }
}

.ce-industrySelectModal {
  .ce-industry {
    width: 100px;
    padding: @space-2;
    border-radius: @border-radius;
    transition: all 250ms ease-in-out;
    cursor: pointer;

    &.active {
      background: @color-light;
      color: @color-mono-100;
    }

    img {
      width: 100%;
      border-radius: @border-radius;
    }
  }
}

.ce-contentPromotionSnippet {
  text-align: left !important;
  display: table-row;

  p {
    width: 100%;
  }

  .ce-contentPromotionSnippet-imageContainer {
    display: none;
  }
}

.ce-qrContainer {
  @media only screen and (max-width: @breakpoint-sm) {
    display: none;
  }

  > div:first-child {
    text-align: center;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;

    a {
      img {
        border: 1px solid @color-mono-70;
        display: inline-block;
        border-radius: @border-radius;
      }
    }

    a:last-of-type {
      display: block;
      max-width: 120px;
    }

    > div {
      max-width: 150px;
      font-weight: bold;
      transform: rotateZ(-15deg) translate(15px, -40px);
    }
  }
}
</style>
